import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Info, Save } from 'lucide-react';
import { invoke } from '@tauri-apps/api/core';
import { Modal } from './ui/Modal';
import { useNotificationStore } from '../store/notificationStore';
import { guardNewVaultItems } from '../store/vaultStore';
import { VAULT_TYPE_META } from './VaultItemModal';

/**
 * Confirm-import for a secret received in chat: editable name + a read-only
 * summary (never the secret values themselves), then vault_put_item into the
 * recipient's own vault with a provenance line appended to the notes.
 *
 * `payload` is the decrypted share ({ type, name, data }) held in the card's
 * component state — this modal keeps it out of any store too.
 */
export default function VaultShareSaveModal({ isOpen, onClose, payload, senderName }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setName(payload?.name || '');
    setSaving(false);
    setError('');
  }, [isOpen, payload]);

  if (!payload) return null;

  const typeMeta = VAULT_TYPE_META[payload.type] || VAULT_TYPE_META.custom;
  const d = payload.data || {};

  // Read-only summary rows — identity fields only, no secret values.
  const summary = [];
  const push = (label, value) => {
    if (value == null || value === '') return;
    summary.push([label, String(value)]);
  };
  if (payload.type === 'sftp') {
    push(t('vaultShare.fieldProtocol', 'Protocol'), (d.protocol || '').toUpperCase());
    push(t('vaultShare.fieldHost', 'Host'), d.host);
    push(t('vaultShare.fieldPort', 'Port'), d.port);
    push(t('vaultShare.fieldUsername', 'Username'), d.username);
  } else if (payload.type === 'password') {
    push(t('vaultShare.fieldUrl', 'URL'), d.url);
    push(t('vaultShare.fieldUsername', 'Username'), d.username);
  } else if (payload.type === 'authHeader') {
    push(t('vaultShare.fieldHeaderName', 'Header name'), d.headerName);
    push(t('vaultShare.fieldSourceEnv', 'Environment'), d.sourceEnv);
  } else if (payload.type === 'custom') {
    const count = Array.isArray(d.fields) ? d.fields.length : 0;
    push(
      t('vaultShare.summaryFields', 'Fields'),
      t('vaultShare.fieldCount', { defaultValue: '{{count}} fields', count })
    );
  }

  const includesSecrets =
    payload.type === 'sftp' && (d.password || d.privateKey)
      ? t('vaultShare.includesSftpSecrets', 'Includes the password / private key')
      : null;

  const doSave = async () => {
    if (saving) return;
    setSaving(true);
    setError('');
    try {
      const data = { ...(payload.data || {}) };
      const stamp = t('vaultShare.sharedByNote', {
        defaultValue: 'Shared by {{name}} via chat on {{date}}',
        name: senderName || t('vaultShare.someone', 'someone'),
        date: new Date().toLocaleDateString(),
      });
      data.notes = data.notes ? `${data.notes}\n${stamp}` : stamp;
      // Plans v2: a saved DB connection / host counts against the plan's cap.
      await guardNewVaultItems([{ type: payload.type }]);
      await invoke('vault_put_item', {
        item: {
          type: payload.type,
          name: name.trim() || payload.name || t('vaultShare.untitled', 'Shared secret'),
          data,
        },
      });
      useNotificationStore.getState().success(t('vaultShare.savedToVault', 'Saved to vault'));
      onClose();
    } catch (e) {
      setError(String(e?.message || e) || t('vaultShare.saveFailed', 'Failed to save to vault'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('vaultShare.saveTitle', 'Save to your vault')}
      customIcon={<Save size={22} className="text-blue-500" />}
    >
      <div className="space-y-3">
        <div>
          <p className="modal-logo-label" style={{ marginBottom: '0.375rem' }}>
            {t('vaultShare.nameLabel', 'Name')}
          </p>
          <input
            className="modal-input modal-input-no-focus-outline"
            value={name}
            autoFocus
            onChange={(e) => setName(e.target.value)}
            placeholder={t('vaultShare.namePlaceholder', 'Item name')}
          />
        </div>

        <div className="rounded-lg border border-gray-200 dark:border-zinc-700 px-3 py-2">
          <div className="text-xs font-medium text-gray-500 dark:text-zinc-400" style={{ marginBottom: 4 }}>
            {t(typeMeta.labelKey)}
          </div>
          {summary.map(([label, value]) => (
            <div key={label} className="chat-secret-summary-row">
              <span className="chat-secret-summary-label">{label}</span>
              <span className="chat-secret-summary-value">{value}</span>
            </div>
          ))}
          {includesSecrets && (
            <p
              className="text-xs text-gray-400 dark:text-zinc-400"
              style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 6, marginBottom: 0 }}
            >
              <Info size={12} style={{ flexShrink: 0 }} />
              {includesSecrets}
            </p>
          )}
        </div>

        {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}

        <div className="flex gap-2">
          <button
            type="button"
            onClick={doSave}
            disabled={saving}
            className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? t('vaultShare.saving', 'Saving…') : t('vaultShare.saveConfirm', 'Save')}
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="modern-button-secondary flex-1 justify-center"
          >
            {t('common.cancel', 'Cancel')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
